import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import Layout from '@/shared/components/Layout/Layout'

const Home = lazy(() => import('@/modules/marketing/pages/Home'))
const About = lazy(() => import('@/modules/marketing/pages/About'))
const DownloadApp = lazy(() => import('@/modules/marketing/pages/DownloadApp'))
const Login = lazy(() => import('@/modules/auth/pages/Login'))
const SignUp = lazy(() => import('@/modules/auth/pages/SignUp'))
const Profile = lazy(() => import('@/modules/auth/pages/Profile'))
const ForgotPassword = lazy(() => import('@/modules/auth/pages/ForgotPassword'))
const ResetPassword = lazy(() => import('@/modules/auth/pages/ResetPassword'))
const RegionCountries = lazy(() => import('@/modules/countries/pages/RegionCountries'))
const CountryPackages = lazy(() => import('@/modules/countries/pages/CountryPackages'))
const ShopPlans = lazy(() => import('@/modules/plans/pages/ShopPlans'))
const GlobalESIMPlans = lazy(() => import('@/modules/plans/pages/GlobalESIMPlans'))
const Checkout = lazy(() => import('@/modules/checkout/pages/Checkout'))
const MyeSIM = lazy(() => import('@/modules/dashboard/pages/MyeSIM'))
const HelpCentre = lazy(() => import('@/modules/support/pages/HelpCentre'))
const EsimInstallationGuide = lazy(() => import('@/modules/support/pages/EsimInstallationGuide'))
const Contact = lazy(() => import('@/modules/support/pages/Contact'))
const TermsAndConditions = lazy(() => import('@/modules/support/pages/TermsAndConditions'))
const PrivacyPolicy = lazy(() => import('@/modules/support/pages/PrivacyPolicy'))
const InviteFriends = lazy(() => import('@/modules/invite/pages/InviteFriends'))
const Blog = lazy(() => import('@/modules/blog/pages/Blog'))
const BlogPostDetail = lazy(() => import('@/modules/blog/pages/BlogPostDetail'))
const AdminBlogManagement = lazy(() => import('@/modules/blog/pages/AdminBlogManagement'))
const Faq = lazy(() => import('@/modules/faq/pages/Faq'))
const AdminFaqManagement = lazy(() => import('@/modules/faq/pages/AdminFaqManagement'))
const AdminLayout = lazy(() => import('@/modules/admin/components/AdminLayout'))
const AdminLogin = lazy(() => import('@/modules/admin/pages/AdminLogin'))
const AdminDashboard = lazy(() => import('@/modules/admin/pages/AdminDashboard'))
const ApiKeyManagement = lazy(() => import('@/modules/admin/pages/ApiKeyManagement'))
const UserManagement = lazy(() => import('@/modules/admin/pages/UserManagement'))
const OrderManagement = lazy(() => import('@/modules/admin/pages/OrderManagement'))
const ProvisioningPending = lazy(() => import('@/modules/admin/pages/ProvisioningPending'))
const FraudManagement = lazy(() => import('@/modules/admin/pages/FraudManagement'))
const AuthSessionSettings = lazy(() => import('@/modules/admin/pages/AuthSessionSettings'))
const PromoManagement = lazy(() => import('@/modules/admin/pages/PromoManagement'))
const EsimManagement = lazy(() => import('@/modules/admin/pages/EsimManagement'))
const NomadManagement = lazy(() => import('@/modules/admin/pages/NomadManagement'))
const NewsletterSubscriptions = lazy(() => import('@/modules/admin/pages/NewsletterSubscriptions'))
const CatalogMirrorAdmin = lazy(() => import('@/modules/admin/pages/CatalogMirrorAdmin'))

function App() {
  return (
    <Router>
      <Suspense fallback={null}>
        <Routes>
          {/* Admin Login - Separate from layout */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin routes - Protected layout (requires authentication) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="newsletter" element={<NewsletterSubscriptions />} />
            <Route path="orders" element={<OrderManagement />} />
            <Route path="provisioning-pending" element={<ProvisioningPending />} />
            <Route path="fraud" element={<FraudManagement />} />
            <Route path="promos" element={<PromoManagement />} />
            <Route path="auth-settings" element={<AuthSessionSettings />} />
            <Route path="esims" element={<EsimManagement />} />
            <Route path="catalog/*" element={<CatalogMirrorAdmin />} />
            <Route path="blog" element={<AdminBlogManagement />} />
            <Route path="faq" element={<AdminFaqManagement />} />
            <Route path="api-keys" element={<ApiKeyManagement />} />
            <Route path="nomad" element={<NomadManagement />} />
          </Route>

          {/* All other routes with Layout (frontend navbar/footer) */}
          <Route element={<Layout />}>
            {/* Marketing routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/download" element={<DownloadApp />} />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<Profile />} />

            {/* Countries routes */}
            <Route path="/region/:regionName" element={<RegionCountries />} />
            <Route path="/country/:countryName" element={<CountryPackages />} />

            {/* Plans routes */}
            <Route path="/shop" element={<ShopPlans />} />
            <Route path="/global-esim" element={<GlobalESIMPlans />} />

            {/* Checkout route */}
            <Route path="/checkout" element={<Checkout />} />

            {/* Dashboard route */}
            <Route path="/my-esim" element={<MyeSIM />} />

            {/* Support routes */}
            <Route path="/help" element={<HelpCentre />} />
            <Route path="/install-esim" element={<EsimInstallationGuide />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/invite-friends" element={<InviteFriends />} />

            {/* Blog routes */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />

            {/* FAQ routes */}
            <Route path="/faq" element={<Faq />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  )
}

export default App
